import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import classnames from 'classnames';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpSharedAuctionCountdown } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdown';

// types
import { PdpSharedAuctionPreview_item$key as Item } from './__generated__/PdpSharedAuctionPreview_item.graphql';

// styles
import styles from './PdpSharedAuctionPreview.scss';
import variantStyles from '../PdpSharedAuctionActions/PdpSharedAuctionActionsTestVariant.scss';
import dibsCss from 'dibs-css';

type Props = {
    item: Item;
    currency: string;
    promoCode: ReactNode;
    isAuctionSiderailRedesignVariant?: boolean | null;
};

const itemFragment = graphql`
    fragment PdpSharedAuctionPreview_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionPricing_item @arguments(page: pdp)
        ...OriginalListPrice_item
        ...PdpSharedAuctionCountdown_item
    }
`;

const copy = {
    startingBid: (
        <FormattedMessage id="PdpSharedAuctionPreview.startingBid" defaultMessage="Starting Bid" />
    ),
};

export const PdpSharedAuctionPreview: FC<Props> = ({
    item: itemRef,
    currency,
    promoCode,
    isAuctionSiderailRedesignVariant,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { buyItNowAmount, hasReserve } = useAuctionPricing({
        item,
        currency,
    });
    const { isMobile } = useServerVarsContext();

    if (isAuctionSiderailRedesignVariant) {
        return (
            <>
                <div className={variantStyles.infoGroupContainer}>
                    {!isMobile && (
                        <div className={dibsCss.sassyFontBodySmallHeavy}>
                            <PdpSharedAuctionCountdown
                                isAuctionSiderailRedesignVariant
                                item={item}
                            />
                        </div>
                    )}
                    <div>
                        <OriginalListPrice
                            item={item}
                            currency={currency}
                            isAuctionSidebarTestVariant
                            className={classnames(variantStyles.originalListPriceVariant, {
                                [dibsCss.sassyFontBodyMediumHeavy]: isMobile,
                                [dibsCss.sassyFontBodySmallHeavy]: !isMobile,
                            })}
                        />
                    </div>
                </div>

                <div className={variantStyles.previewContainer}>
                    <div className={classnames(variantStyles.infoGroup, dibsCss.mbXxsmall)}>
                        <div className={variantStyles.infoHeader}>{copy.startingBid}</div>
                        <div className={variantStyles.infoDetail}>
                            <PriceDisplay currency={currency} item={item} sku={null} useSvgIcon />
                        </div>
                    </div>
                    <div className={variantStyles.infoGroup}>
                        <div className={variantStyles.infoHeader}>
                            <FormattedMessage
                                id="PdpSharedAuctionPreview.buyItNow"
                                defaultMessage="Buy It Now"
                            />
                            <PdpSharedAuctionTooltipLazy type="buyItNow" />
                        </div>
                        <div
                            className={variantStyles.infoDetail}
                            data-tn="pdp-item-buy-it-now-price"
                        >
                            {buyItNowAmount && (
                                <FormattedPrice value={buyItNowAmount} currency={currency} />
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className={styles.container}>
            <OriginalListPrice
                item={item}
                currency={currency}
                className={styles.originalListPrice}
            />
            {!hasReserve && (
                <span className={styles.noReserve}>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.noReserve"
                        defaultMessage="No Reserve"
                    />
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <span className={styles.bid}>{copy.startingBid}</span>
            <PriceDisplay currency={currency} item={item} useSvgIcon />
            {buyItNowAmount && (
                <span>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.currentBid"
                        defaultMessage="{amount} Buy It Now"
                        values={{
                            amount: <FormattedPrice value={buyItNowAmount} currency={currency} />,
                        }}
                    />
                </span>
            )}
            <div className={styles.promoCode}>{promoCode}</div>
            <div className={styles.prompt}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.prompt"
                    defaultMessage="You can't bid on this item yet. Favorite this item to get updates on this auction. {link}"
                    values={{
                        link: (
                            <Link
                                href="/info/auctions-faq/"
                                target="_blank"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionPreview.learnMore"
                                    defaultMessage="Learn More about Auctions"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        </div>
    );
};
